import { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, initSessionRefresh } from '../lib/supabase';
import { isAuthRoute } from '../lib/isAuthRoute';
import { interceptRecoveryToken } from '../lib/recoveryTokenInterceptor';
import { log } from '../lib/logger';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  signUp: (email: string, password: string, fullName: string) => Promise<{ data: any; error: any }>;
  signIn: (email: string, password: string) => Promise<{ data: any; error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!user) {
      setIsAdmin(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('user_roles')
        .select('role, can_manage_users')
        .eq('user_id', user.id)
        .maybeSingle();
      if (cancelled) return;
      setIsAdmin(data?.role === 'admin' || data?.can_manage_users === true);
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    const stopSessionRefresh = initSessionRefresh();
    const checkSession = async () => {
      if (interceptRecoveryToken()) {
        log.debug('[AuthContext] Recovery token intercepted, waiting for redirect');
        return;
      }

      const pathname = window.location.pathname;
      const skipAuth = pathname === '/onboarding' || isAuthRoute(pathname);

      if (skipAuth) {
        log.debug(`[AuthContext] On ${pathname}, skipping session check to preserve tokens`);
        setLoading(false);
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      log.debug('Session loaded:', { hasSession: !!session, hasUser: !!session?.user });
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      log.debug(`[AuthContext] Auth state changed: ${event}`);

      const pathname = window.location.pathname;
      const skipAuth = pathname === '/onboarding' || isAuthRoute(pathname);

      if (skipAuth) {
        log.debug(`[AuthContext] On ${pathname}, skipping auth state change`);
        return;
      }

      // Handle session refresh and token updates
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        log.debug(`[AuthContext] ${event} - Session updated, triggering data refresh`);
        // Note: In a future implementation with React Query, we would invalidate queries here:
        // queryClient.invalidateQueries({ queryKey: ['user-roles', session?.user?.id] });
        // queryClient.invalidateQueries({ queryKey: ['pending-invites'] });
        // queryClient.invalidateQueries({ queryKey: ['active-users'] });

        // For now, force a page reload to refresh all data after token refresh
        if (event === 'TOKEN_REFRESHED' && session?.user) {
          log.debug('[AuthContext] Token refreshed successfully, data will be re-fetched on next interaction');
        }
      }

      if (event === 'SIGNED_OUT') {
        log.debug('[AuthContext] User signed out, clearing session');
        // Clear any cached data
        setSession(null);
        setUser(null);
      } else {
        setSession(session);
        setUser(session?.user ?? null);
      }

      setLoading(false);
    });

    return () => { subscription.unsubscribe(); stopSessionRefresh(); };
  }, []);

  const signUp = async (email: string, password: string, fullName: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: {
          full_name: fullName,
        },
      },
    });
    return { data, error };
  };

  const signIn = async (email: string, password: string) => {
    log.debug('[AuthContext] Attempting sign in with:', { email });
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      log.error('[AuthContext] Sign in error:', error);
    } else {
      log.debug('[AuthContext] Sign in successful:', {
        hasUser: !!data.user,
        hasSession: !!data.session,
        userId: data.user?.id
      });
    }

    return { data, error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isAdmin, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
